import { BarChartOutlined, CloudDownloadOutlined } from '@ant-design/icons'
import type { Chart } from '@antv/g2'
import { useDownload } from '@web/react-common'
import { Button, DatePicker, InputNumber, Typography } from 'antd'
import moment from 'moment'
import React from 'react'

import { useGetFeaturedJournalLineChartQuery } from '@/generated/graphql'

import s from '../kpi.module.less'

export const StatisticsFeaturedJournalLineChart = () => {
  const title = '访问趋势'
  const divId = 'featuredJournalLineChartDiv'

  const [timeRange, setTimeRange] = React.useState<[moment.Moment, moment.Moment]>([
    moment().subtract(1, 'd').set('m', 0).set('s', 0),
    moment().set('m', 0).set('s', 0),
  ])
  const [session, setSession] = React.useState<number>(5) // 有记录的精选周刊从第 5 期开始

  const [chart, setChart] = React.useState<Chart>()

  const ref = React.useRef<HTMLDivElement>(null)

  const { data } = useGetFeaturedJournalLineChartQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      startTime: timeRange[0].toLocaleString(),
      endTime: timeRange[1].toLocaleString(),
      session,
    },
  })

  const statistics = data?.statistics.featuredJournalTrendData || []

  React.useEffect(() => {
    import('../chart.component').then(({ default: Chart }) => {
      const newChart = new Chart({
        container: divId,
        autoFit: true,
        syncViewPadding: true,
      })
      newChart.animate(false)
      setChart(newChart)
    })
  }, [])

  React.useEffect(() => {
    if (statistics && chart) {
      chart.clear()
      chart.data(statistics)
      chart.scale({
        date: {
          range: [0, 1],
        },
        count: {
          sync: true,
          nice: true,
        },
      })

      chart.tooltip({
        showCrosshairs: true,
        shared: true,
        title: (_, data) => data.date + ' 时',
      })

      chart.axis('date', {
        tickLine: {
          alignTick: true,
          length: 6,
        },
      })

      chart.line().position('date*count').color('tag').shape('line')

      chart.point().position('date*count').color('tag')

      chart.render()
    }
  }, [chart, statistics])

  const fileName = `Tale 精选周刊访问趋势图 - ${session} 期 (${timeRange[0].format(
    'YYYY-MM-DD HH:mm'
  )} - ${timeRange[1].format('YYYY-MM-DD HH:mm')}).csv`

  const file = [['时间', '访问量']].concat(
    statistics.length > 0 ? statistics.map((data) => [data.date + ' 时', data.count.toString()]) : [['', '']]
  )

  const header = (title: string) => (
    <div style={{ display: 'flex', fontSize: 32, margin: '16px 0 8px 0', justifyContent: 'center' }}>
      <BarChartOutlined />
      <Typography.Title style={{ marginLeft: 8 }} level={3}>
        {title}
      </Typography.Title>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Button icon={<CloudDownloadOutlined />} type='link' onClick={() => useDownload(file, fileName)}>
          下载
        </Button>
      </div>
    </div>
  )

  return (
    <div className={s.chartContainer}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <DatePicker.RangePicker
          showTime={{ format: 'HH:00' }}
          format='YYYY-MM-DD HH:00'
          value={timeRange}
          onChange={(value) => {
            if (value && value[0] && value[1]) {
              const startTime = value[0].clone().set('m', 0).set('s', 0)
              const endTime = value[1].clone().set('m', 0).set('s', 0)
              setTimeRange([startTime, endTime])
            }
          }}
        />
        <div style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: 4 }}>{`周期:`}</span>
          <InputNumber precision={0} value={session} onChange={(value) => setSession(Math.round(value))} />
        </div>
      </div>

      {header(title)}
      <div className={s.chartBox}>
        <div id={divId} className={s.chart} ref={ref} />
      </div>
    </div>
  )
}
